import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Event,
  EventType,
  EventUser,
  EventContact,
  EventTag,
  CreateEventInput,
  UpdateEventInput,
  CreateEventUserInput,
  UpdateEventUserInput,
  AddEventContactInput,
  UpdateEventContactInput,
  CreateEventTagInput,
  UpdateEventTagInput,
  EventVendorRecipient,
  SendVendorEmailInput,
  EventOverview,
} from '@peles/shared';
import { eventDayChanged, requestTaskDateShift } from '../utils/taskDateShiftRequests';
import * as eventsApi from '../api/events';

export function useEvents(filters?: eventsApi.EventListQuery) {
  return useQuery<Event[]>({
    queryKey: ['events', filters],
    queryFn: () => eventsApi.listEvents(filters),
  });
}

export function useEvent(id: string) {
  return useQuery<Event>({
    queryKey: ['events', id],
    queryFn: () => eventsApi.getEvent(id),
    enabled: !!id,
  });
}

/**
 * Root of the overview query keys. The numbers are drawn from expenses,
 * payments, guests, tasks and photos, so no single mutation hook owns them:
 * the app's `MutationCache` (`createAppQueryClient`) invalidates this key
 * after every successful mutation instead.
 */
export const EVENT_OVERVIEW_KEY = 'event-overview';

export function useEventOverview(id: string | undefined) {
  return useQuery<EventOverview>({
    queryKey: [EVENT_OVERVIEW_KEY, id],
    queryFn: () => eventsApi.getEventOverview(id!),
    enabled: !!id,
  });
}

/** Overviews for a page of events, keyed so a page change fetches only that page. */
export function useEventOverviews(ids: string[], enabled = true) {
  return useQuery<EventOverview[]>({
    queryKey: [EVENT_OVERVIEW_KEY, 'batch', ids],
    queryFn: () => eventsApi.listEventOverviews(ids),
    enabled: enabled && ids.length > 0,
    staleTime: 60_000,
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventInput) => eventsApi.createEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateEventInput }) =>
      eventsApi.updateEvent(id, updates),
    onSuccess: ({ previousStartAt, ...updatedEvent }) => {
      queryClient.setQueryData(['events', updatedEvent.id], updatedEvent);
      queryClient.invalidateQueries({ queryKey: ['events'] });
      // The server names the start the event had until this save — never the
      // cache's, which a tab left open while somebody else moved the event
      // would have wrong. A new day offers to move the tasks that follow it.
      if (previousStartAt && eventDayChanged(previousStartAt, updatedEvent.startAt)) {
        requestTaskDateShift({
          eventId: updatedEvent.id,
          from: new Date(previousStartAt).toISOString(),
        });
      }
    },
  });
}

export function useCancelEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => eventsApi.cancelEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useEventTypes() {
  return useQuery<EventType[]>({
    queryKey: ['event-types'],
    queryFn: () => eventsApi.listEventTypes(),
    staleTime: Infinity,
  });
}

export function useEventUsers(eventId: string) {
  return useQuery<EventUser[]>({
    queryKey: ['events', eventId, 'users'],
    queryFn: () => eventsApi.listEventUsers(eventId),
    enabled: !!eventId,
  });
}

export function useAddEventUser(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventUserInput) => eventsApi.addEventUser(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'users'] });
    },
  });
}

export function useUpdateEventUser(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: UpdateEventUserInput }) =>
      eventsApi.updateEventUser(eventId, userId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'users'] });
    },
  });
}

export function useRemoveEventUser(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => eventsApi.removeEventUser(eventId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'users'] });
    },
  });
}

// ─── Event Contacts ─────────────────────────────────────────────────────────

export function useEventContacts(eventId: string) {
  return useQuery<EventContact[]>({
    queryKey: ['events', eventId, 'contacts'],
    queryFn: () => eventsApi.listEventContacts(eventId),
    enabled: !!eventId,
  });
}

export function useAddEventContact(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddEventContactInput) => eventsApi.addEventContact(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'contacts'] });
    },
  });
}

export function useUpdateEventContact(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, updates }: { contactId: string; updates: UpdateEventContactInput }) =>
      eventsApi.updateEventContact(eventId, contactId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'contacts'] });
    },
  });
}

export function useRemoveEventContact(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactId: string) => eventsApi.removeEventContact(eventId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'contacts'] });
    },
  });
}

// ─── Event Tags ─────────────────────────────────────────────────────────────

export function useEventTags(eventId: string | undefined) {
  return useQuery<EventTag[]>({
    queryKey: ['events', eventId, 'tags'],
    queryFn: () => eventsApi.listEventTags(eventId!),
    enabled: !!eventId,
  });
}

export function useCreateEventTag(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventTagInput) => eventsApi.createEventTag(eventId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'tags'] });
    },
  });
}

export function useUpdateEventTag(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tagId, updates }: { tagId: string; updates: UpdateEventTagInput }) =>
      eventsApi.updateEventTag(eventId, tagId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'tags'] });
    },
  });
}

export function useDeleteEventTag(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: string) => eventsApi.deleteEventTag(eventId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'tags'] });
    },
  });
}

// ─── Event Vendor Email ─────────────────────────────────────────────────────

export function useEventVendorRecipients(eventId: string | undefined) {
  return useQuery<EventVendorRecipient[]>({
    queryKey: ['events', eventId, 'vendor-recipients'],
    queryFn: () => eventsApi.getEventVendorRecipients(eventId!),
    enabled: !!eventId,
  });
}

export function useSendVendorEmail(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SendVendorEmailInput) => eventsApi.sendVendorEmail(eventId, input),
    // A letter may have opened a vendor's conversation; conversation badges recount.
    onSuccess: (result) => {
      if ((result.conversations ?? []).length > 0) {
        void queryClient.invalidateQueries({ queryKey: ['thread-counts'] });
      }
    },
  });
}
